import { useEffect, useState } from "react";
import Button from "@/components/base/Button";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { issueCertificate, listCertificates, type Certificate } from "@/services/certificateService";
import { getSchoolInfo } from "@/services/schoolService";
import type { Enrollment, Student } from "@/types";
import { exportCertificatePdf } from "@/utils/certificatePdf";
import { formatDate } from "@/utils/format";
import { exportStudentCardsPdf } from "@/utils/studentCardPdf";

interface Props {
  student: Student;
  /** Inscription de l'année consultée (aucune : pas de carte ni de certificat). */
  enrollment: Enrollment | null;
  className: string;
}

/** Documents de l'élève : carte scolaire et certificats de scolarité (nouveau ou réimpression). */
export default function StudentDocuments({ student, enrollment, className }: Props) {
  const { notify } = useToast();
  const { can } = useAuth();
  const canCards = can("documents:cards");
  const canCertificates = can("documents:certificates");
  const [certificates, setCertificates] = useState<Certificate[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!canCertificates) return;
    listCertificates(student.id)
      .then(setCertificates)
      .catch(() => setCertificates([]));
  }, [student.id, canCertificates]);

  if (!canCards && !canCertificates) return null;

  const run = async (key: string, task: () => Promise<void>) => {
    setBusy(key);
    try {
      await task();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Document impossible à générer.", "error");
    } finally {
      setBusy(null);
    }
  };

  const printCard = () =>
    run("card", async () => {
      if (!enrollment) return;
      await exportStudentCardsPdf(
        [
          {
            firstName: student.firstName,
            lastName: student.lastName,
            matricule: student.matricule,
            gender: student.gender,
            birthDate: student.birthDate,
            birthPlace: student.birthPlace,
            className,
            photo: student.photo,
          },
        ],
        await getSchoolInfo(),
        enrollment.schoolYear,
        `carte-${student.matricule}.pdf`,
      );
    });

  const newCertificate = () =>
    run("new", async () => {
      if (!enrollment) return;
      const certificate = await issueCertificate(enrollment.id);
      await exportCertificatePdf(certificate, await getSchoolInfo());
      setCertificates((prev) => [certificate, ...(prev ?? [])]);
      notify(`Certificat ${certificate.number} délivré.`);
    });

  const reprint = (certificate: Certificate) =>
    run(certificate.id, async () => exportCertificatePdf(certificate, await getSchoolInfo()));

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">Documents</p>
      <div className="rounded-md border border-background-200 p-3.5">
        {enrollment ? (
          <div className="flex flex-wrap gap-2">
            {canCards && (
              <Button size="sm" variant="outline" icon="ri-bank-card-line" onClick={printCard} disabled={busy !== null}>
                {busy === "card" ? "Préparation..." : "Carte scolaire"}
              </Button>
            )}
            {canCertificates && (
              <Button size="sm" icon="ri-award-line" onClick={newCertificate} disabled={busy !== null}>
                {busy === "new" ? "Délivrance..." : "Délivrer un certificat de scolarité"}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-xs text-foreground-500">
            Pas d'inscription active pour l'année consultée : ni carte ni certificat ne peuvent être produits.
          </p>
        )}

        {canCertificates && certificates && certificates.length > 0 && (
          <ul className="mt-3 divide-y divide-background-200 border-t border-background-200">
            {certificates.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <p className="text-sm font-medium text-foreground-900">{c.number}</p>
                  <p className="text-xs text-foreground-500">
                    {c.className} · {c.schoolYear} · délivré le {formatDate(c.createdAt)} par {c.issuedByName}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="ri-printer-line"
                  onClick={() => void reprint(c)}
                  disabled={busy !== null}
                >
                  Réimprimer
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
